import React, { useEffect } from "react";
import PropTypes from "prop-types";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

export default function AboutPage({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "About | Bandarban University",
}) {
  // Update Browser Tab Title
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const rootStyle = {
    "--accent": accent,
    backgroundImage: `linear-gradient(135deg, var(--accent) 0%, rgba(17,94,50,0.85) 40%, rgba(255,255,255,0.03) 100%)`,
  };

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative w-full overflow-hidden px-6 sm:px-8 lg:px-16"
        style={rootStyle}
      >
        <div className="relative max-w-7xl mx-auto flex flex-col items-center text-center py-20 lg:py-28">
          <img
            src={logoSrc}
            alt="Logo"
            className="w-28 h-28 object-contain drop-shadow-md mb-6"
          />

          <h1 className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
            Welcome to <span style={{ color: "var(--accent)" }}>Bandarban University</span>
          </h1>

          <p className="mt-4 max-w-2xl text-white/90 text-lg sm:text-xl">
            Building bright minds with a strong foundation in knowledge,
            innovation, and community.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href={`${import.meta.env.VITE_API_SOURCE_URL}home/apply-online`}
              className="px-6 py-3 bg-white rounded-xl font-semibold text-sm shadow hover:opacity-95"
              style={{ color: "var(--accent)" }}
            >
              Apply Now
            </a>
            <a
              href="#about"
              className="px-6 py-3 border border-white/40 rounded-xl text-white text-sm hover:bg-white/10"
            >
              Learn More
            </a>
          </div>
        </div>

        {/* Decorative bottom wave */}
        <div className="-mt-6">
          <svg
            viewBox="0 0 1440 80"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-20"
          >
            <path
              d="M0,32 C240,96 480,0 720,32 C960,64 1200,16 1440,32 L1440 80 L0 80 Z"
              fill="white"
              opacity="0.92"
            />
          </svg>
        </div>
      </section>

      {/* Sub Section Content Body - Long Article */}
      <section className="bg-white py-16 px-6 sm:px-10 lg:px-20">
        <div className="max-w-4xl mx-auto">
          <h2
            className="text-3xl md:text-4xl font-bold text-center"
            style={{ color: "var(--accent)" }}
          >
            About Bandarban University
          </h2>
		    <p align="justify" className="mt-6 text-gray-700 leading-relaxed text-lg">
				 Bandarban University is the first Public-Private Partnership (PPP) university in Bangladesh. It is approved by the Government of Bangladesh and the University Grants Commission (UGC). Established in the lush greenery of Bandarban Hill District, the university provides a serene, and ideal environment for learning and academic excellence. Notably, students from 12 different ethnic communities study here, making it a unique hub of diversity, inclusion, and cultural harmony.
			</p>
			 <div className="my-10">
				<img
				  src="/assets/img/BU-Campus.jpg"
				  alt="Bandarban University Campus"
				  className="w-full rounded-xl shadow-lg"
				/>
				<p className="mt-3 text-center text-gray-500 text-sm">
				  Bandarban University Campus surrounded by hills and nature
				</p>
			  </div>
			<p align="justify" className="mt-4 text-gray-700 leading-relaxed text-lg">
				Bandarban University Campus is surrounded by hills and nature, creating a peaceful and inspiring atmosphere for students and faculty members alike.
			</p>
			<p align="justify" className="mt-4 text-gray-700 leading-relaxed text-lg">
				The idea of establishing a university in Bandarban emerged from a long-standing vision to expand higher education opportunities in the Chattogram Hill Tracts region. With strong institutional initiatives and collaboration, this vision gradually turned into reality.
			</p>
			<p align="justify" className="mt-4 text-gray-700 leading-relaxed text-lg">
				A foundation named “Bandarban Shikkha O Unnayan Foundation” (Bandarban Education & Development Foundation) was formed, consisting of members from civil society, distinguished business leaders, government and non-government officials, local representatives, and renowned academicians from different public and private universities. The foundation played a crucial role in laying the groundwork for the university.
			</p>
			<p align="justify" className="mt-4 text-gray-700 leading-relaxed text-lg">
				The foundation signed a Memorandum of Understanding (MoU) with the Bandarban Hill District Council (BHDC) to establish a Public-Private Partnership (PPP) university in Bandarban, named “Bandarban University.” Dedicated academics and professionals worked together to prepare the necessary documentation and fulfill all formal requirements.
			</p>
			<p align="justify" className="mt-4 text-gray-700 leading-relaxed text-lg">
				With the support and approval of the Government of Bangladesh and the University Grants Commission (UGC), Bandarban University officially received its approval in 2018. The university was formally inaugurated on 7th February 2019. From 1st January 2026 Bandarban University has been running its academic and administrative activities at the 100 acre own campus at K.B road, Sualok, Bandarban Sadar, Bandarban.
			</p>
			<p align="justify" className="mt-4 text-gray-700 leading-relaxed text-lg">
				Since its inception, Bandarban University has been committed to ensuring a high-quality learning environment equipped with modern facilities and teaching aids. It has opened a new horizon of higher education for students across Bangladesh, especially for those in the Chattogram Hill Tracts. Today, the university stands as a symbol of hope, opportunity, and progress for the people of the region, particularly of Bandarban.
			</p>
        </div>
      </section>
    </div>
  );
}

AboutPage.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
  pageTitle: PropTypes.string,
};

